import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Sparkle, Check } from "@phosphor-icons/react";

export default function RegisterPage() {
  const { register, formatApiError } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const planId = params.get("plan");
  const interval = params.get("interval") === "year" ? "year" : "month";

  const [plans, setPlans] = useState({});
  const [seatAddon, setSeatAddon] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!planId) return;
    api.get("/subscription/plans").then((r) => {
      setPlans(r.data.plans || {});
      setSeatAddon(r.data.seat_addon || null);
    }).catch(() => {});
  }, [planId]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      await register(form.email, form.password, form.name);
      // If a plan was pre-selected on the landing page, kick straight to Stripe checkout.
      if (planId && plans[planId]) {
        toast.success("Account created. Redirecting to secure checkout…");
        try {
          const { data } = await api.post("/subscription/checkout", {
            plan: planId,
            interval,
            success_url: `${window.location.origin}/subscription?checkout=success&session_id={CHECKOUT_SESSION_ID}`,
            cancel_url: `${window.location.origin}/subscription?checkout=cancel`,
          });
          window.location.href = data.checkout_url;
          return;
        } catch (co) {
          toast.error("Account ready — please pick a plan.");
          navigate("/subscription");
          return;
        }
      }
      toast.success("Account created");
      navigate("/dashboard");
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  const chosen = planId ? plans[planId] : null;
  const price = chosen?.prices?.[interval];

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="w-full max-w-5xl grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">

        {/* Left: Register form */}
        <div className="qb-card p-8">
          <Link to="/" className="flex items-center gap-2 mb-8" data-testid="register-back-home">
            <div className="w-8 h-8 rounded-lg bg-primary text-primary-foreground flex items-center justify-center">
              <Sparkle size={18} weight="fill" />
            </div>
            <span className="text-lg font-bold">LedgerLoom</span>
          </Link>

          <h1 className="text-3xl font-bold tracking-tight">Create your account</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {chosen
              ? `You'll be sent to secure Stripe checkout right after this step to subscribe to ${chosen.name}.`
              : "A starter company and chart of accounts will be created automatically."}
          </p>

          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <Label htmlFor="name" className="text-xs font-medium">Full name</Label>
              <Input
                id="name"
                data-testid="register-name"
                value={form.name}
                onChange={set("name")}
                required
                className="mt-1 h-11 rounded-md"
              />
            </div>
            <div>
              <Label htmlFor="email" className="text-xs font-medium">Work email</Label>
              <Input
                id="email"
                type="email"
                data-testid="register-email"
                value={form.email}
                onChange={set("email")}
                required
                className="mt-1 h-11 rounded-md"
              />
            </div>
            <div>
              <Label htmlFor="pw" className="text-xs font-medium">Password (min 6 chars)</Label>
              <Input
                id="pw"
                type="password"
                data-testid="register-password"
                value={form.password}
                onChange={set("password")}
                required
                minLength={6}
                className="mt-1 h-11 rounded-md"
              />
            </div>

            {err && (
              <div
                data-testid="register-error"
                className="text-sm text-destructive border border-destructive/40 bg-destructive/5 rounded-md px-3 py-2"
              >
                {err}
              </div>
            )}

            <Button
              type="submit"
              disabled={busy}
              data-testid="register-submit"
              className="w-full h-11 rounded-md text-sm font-semibold"
            >
              {busy ? "Creating…" : chosen ? `Create account & continue to Stripe` : "Create account"}
            </Button>

            <div className="text-xs text-muted-foreground text-center">
              Already registered?{" "}
              <Link to="/login" className="underline text-foreground">
                Sign in
              </Link>
            </div>
          </form>
        </div>

        {/* Right: Selected plan summary (only when ?plan=) */}
        {chosen && (
          <div className="qb-card p-8 sticky top-6" data-testid="register-plan-summary">
            <div className="overline">Your selected plan</div>
            <div className="mt-2 flex items-baseline justify-between">
              <div className="text-2xl font-bold">{chosen.name}</div>
              <div className="text-xs text-muted-foreground uppercase tracking-widest">{interval}ly</div>
            </div>
            <div className="text-xs text-muted-foreground">{chosen.tagline}</div>

            <div className="mt-6 pb-6 border-b border-border">
              <div className="text-4xl font-bold tracking-tight num">{price?.label}</div>
              {price?.note && <div className="text-xs text-primary mt-1">{price.note}</div>}
            </div>

            <ul className="mt-6 space-y-2 text-sm">
              {chosen.features.map((f) => (
                <li key={f} className="flex items-start gap-2">
                  <Check size={16} className="text-primary shrink-0 mt-0.5" weight="bold" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>

            <div className="mt-6 text-xs text-muted-foreground">
              You'll be redirected to <b>checkout.stripe.com</b> to enter payment details. Cancel anytime — data export is always free.
            </div>

            <div className="mt-4">
              <Link to="/#pricing" className="text-xs text-muted-foreground underline hover:text-foreground">
                Choose a different plan
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
